import { Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Ticket,
  Building2,
  BarChart3,
  LogOut,
  KeyRound,
  type LucideIcon,
} from 'lucide-react';
import { ModuleKey } from '@sfa/shared';
import { useAuth } from '@/contexts/auth-context';
import { BrandLockup } from '@/components/common/BrandMark';
import { usePermissions } from '@/hooks/usePermissions';

const navSections = [
  {
    title: 'Sales',
    items: [
      {
        to: '/dashboard/producer',
        label: 'Producer Dashboard',
        icon: LayoutDashboard,
        module: ModuleKey.Dashboard,
      },
      {
        // Was mislabelled "Lead Details" while pointing at the list. The real
        // detail page is `/leads/:id` (PAC-38) and needs an actual lead, so it
        // is reached by clicking a row rather than from here.
        to: '/leads',
        label: 'Leads',
        icon: Users,
        module: ModuleKey.Leads,
      },
    ],
  },
  {
    title: 'Management',
    items: [
      {
        to: '/dashboard/management',
        label: 'Management Dashboard (v1)',
        icon: BarChart3,
        module: ModuleKey.Management,
      },
      {
        to: '/dashboard/management-alt',
        label: 'Agency Command Center',
        icon: BarChart3,
        module: ModuleKey.Management,
      },
    ],
  },
  {
    title: 'Service & CRM',
    items: [
      {
        to: '/crm/service',
        label: 'Service Dashboard',
        icon: Ticket,
        module: ModuleKey.CrmService,
      },
      {
        to: '/crm/tickets',
        label: 'Ticket Workspace',
        icon: Ticket,
        module: ModuleKey.CrmService,
      },
      {
        to: '/clients/demo',
        label: 'Household Details',
        icon: Building2,
        module: ModuleKey.Clients,
      },
    ],
  },
];

function NavCard({
  to,
  label,
  icon: Icon,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 transition-colors hover:bg-muted/60"
    >
      <Icon size={16} className="text-primary shrink-0" />
      <span className="text-sm font-medium">{label}</span>
    </Link>
  );
}

export function DevNavPage() {
  const { user, logout } = useAuth();
  const { isOwner, canRead } = usePermissions();

  const visibleSections = navSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => canRead(item.module)),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-4 md:px-6">
        <BrandLockup size="sm" tagline="Screen Navigator" />
        <div className="flex items-center gap-4">
          {user && (
            <span className="max-w-[45vw] truncate text-xs text-muted-foreground">
              {user.email}
            </span>
          )}
          <button
            type="button"
            onClick={logout}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <LogOut size={14} />
            Log out
          </button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl px-4 py-10 md:px-6">
        <p className="mb-8 text-sm text-muted-foreground">
          All 7 Figma mockup screens are wired as routes. Pick a screen to preview — management
          v1 and v2 are both available for comparison.
        </p>

        <div className="space-y-8">
          {visibleSections.map((section) => (
            <section key={section.title}>
              <h2 className="mb-3 text-xs font-medium tracking-wide text-muted-foreground uppercase">
                {section.title}
              </h2>
              <div className="grid gap-2">
                {section.items.map((item) => (
                  <NavCard key={item.to} {...item} />
                ))}
              </div>
            </section>
          ))}

          {isOwner && (
            <section>
              <h2 className="mb-3 text-xs font-medium tracking-wide text-muted-foreground uppercase">
                Administration
              </h2>
              <div className="grid gap-2">
                <NavCard to="/settings/users" label="Agency Users" icon={Users} />
                <NavCard
                  to="/settings/roles"
                  label="Roles & Permissions"
                  icon={KeyRound}
                />
              </div>
            </section>
          )}
        </div>
      </main>
    </div>
  );
}
